import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { toast } from 'sonner';
import { Camera, Upload, X, User, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EditProfileModal = ({ open, onOpenChange, user, onAvatarUpdate }) => {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      toast.error('Invalid file type. Only JPEG and PNG are allowed.');
      return;
    }

    // Validate file size (3MB)
    if (file.size > 3 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 3MB.');
      return;
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await axios.post(`${API}/users/me/avatar`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('Profile picture updated successfully!');
      onAvatarUpdate(response.data.avatar_url);
      handleClose();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to upload profile picture';
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setPreviewUrl(null);
    setSelectedFile(null);
    onOpenChange(false);
  };

  const clearSelection = () => {
    setPreviewUrl(null);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const currentAvatarUrl = user?.avatar_url ? `${BACKEND_URL}${user.avatar_url}` : null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="edit-profile-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1B273D]">
            <User className="h-5 w-5" />
            Edit Profile Picture
          </DialogTitle>
          <DialogDescription>
            Upload a new profile picture to personalize your account.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-6 py-6">
          {/* Current / Preview Avatar */}
          <div className="relative">
            <Avatar className="h-32 w-32 ring-4 ring-[#DC143C]/20 ring-offset-4">
              {previewUrl ? (
                <AvatarImage src={previewUrl} alt="Preview" />
              ) : currentAvatarUrl ? (
                <AvatarImage src={currentAvatarUrl} alt={user?.name} />
              ) : null}
              <AvatarFallback className="bg-gradient-to-br from-[#DC143C] to-[#b91130] text-white text-3xl font-bold">
                {getInitials(user?.name || 'U')}
              </AvatarFallback>
            </Avatar>
            
            {previewUrl && (
              <button
                onClick={clearSelection}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors shadow-lg"
                data-testid="clear-preview-btn"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Upload Instructions */}
          <div className="text-center text-sm text-gray-500">
            <p>Accepted formats: JPEG, PNG</p>
            <p>Maximum size: 3MB</p>
          </div>

          {/* File Input & Actions */}
          <div className="flex flex-col gap-3 w-full">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="avatar-file-input"
            />

            <Button
              variant="outline"
              className="w-full border-2 border-dashed border-gray-300 hover:border-[#DC143C] hover:bg-[#DC143C]/5 py-8 transition-all"
              onClick={() => fileInputRef.current?.click()}
              data-testid="select-image-btn"
            >
              <div className="flex flex-col items-center gap-2">
                <Camera className="h-6 w-6 text-gray-400" />
                <span className="text-gray-600">
                  {selectedFile ? selectedFile.name : 'Click to select an image'}
                </span>
              </div>
            </Button>

            {selectedFile && (
              <Button
                onClick={handleUpload}
                disabled={uploading}
                className="w-full bg-gradient-to-r from-[#DC143C] to-[#b91130] hover:from-[#b91130] hover:to-[#8b0d26] text-white font-semibold"
                data-testid="upload-avatar-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Upload Picture
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditProfileModal;
